/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IContentBlockViewProps } from '@msdyn365-commerce-modules/content-block/src/modules/content-block/./content-block';
import { Modal, ModalBody, ModalHeader, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { RichTextComponent } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import YoutubeIFrame from '../../../components/youtube-iframe';
import { IClickableContainerLinkData, IContentBlockProps, ILinksData } from '../definition-extensions/content-block.ext.props.autogenerated';

interface IContentBlockState extends React.ComponentState {
    isModalOpen: boolean;
}

/**
 *
 * ContentBlockView component
 * @extends {React.PureComponent<IContentBlockViewProps & IContentBlockProps<{}>, IContentBlockState>}
 */
class ContentBlockView extends React.PureComponent<IContentBlockViewProps & IContentBlockProps<{}>, IContentBlockState> {
    constructor(props: IContentBlockViewProps & IContentBlockProps<{}>) {
        super(props);
        this.state = { isModalOpen: false };
        this._toggleModal = this._toggleModal.bind(this);
    }

    public render(): JSX.Element | null {
        const {
            config,
            contentBlockContainer,
            detailsContainer,
            image,
            imageContainer,
            links,
            text,
            title
        } = this.props;
        const clickableContainerLink = config.clickableContainerLink;
        const isCustomTheme = config.colorTheme === 'custom' ? true : false;
        const colorThemeClass = config.colorTheme && `colortheme__${config.colorTheme}`;

        const content = (
            <Module
                {...contentBlockContainer}
                style={isCustomTheme ? { backgroundColor: config.backgroundColor } : {}}
                className={classnames(contentBlockContainer.className, colorThemeClass)}
            >
                <Node {...imageContainer}>
                    {image}
                </Node>
                <Node {...detailsContainer} style={isCustomTheme ? { color: config.textColor } : {}}>
                    {title}
                    {config.paragraphMobile ? this._renderParagraphs(this.props) : text}
                    {isCustomTheme ? this._renderCustomLinks(this.props) : links}
                    {config.youTubeLink && this._renderYouTubeLink(this.props)}
                </Node>
            </Module>
        );

        if (clickableContainerLink?.linkUrl?.destinationUrl) {
            return (
                <a
                    className='ms-content-block__clickable'
                    href={clickableContainerLink.linkUrl.destinationUrl}
                    target={this._target(clickableContainerLink)}
                    aria-label={clickableContainerLink.ariaLabel}
                >
                    {content}
                </a>
            );
        } else {
            return content;
        }
    }

    private _target(link: IClickableContainerLinkData): string | undefined {
        return (link.openInNewTab) ? '_blank' : undefined;
    }

    private _renderParagraphs(props: IContentBlockProps<{}>): JSX.Element | null {
        const { paragraph, paragraphMobile } = props.config;
        return (
            <>
                {paragraph && <RichTextComponent text={paragraph} className='ms-content-block__text desktop' />}
                {paragraphMobile && <RichTextComponent text={paragraphMobile} className='ms-content-block__text mobile' />}
            </>
        );
    }

    private _renderCustomLinks(props: IContentBlockProps<{}>): JSX.Element | null {
        const {
            buttonBackgroundColor,
            buttonBorderColor,
            buttonTextColor,
            links
        } = props.config;

        if (links && links.length > 0) {
            return (
                <div className='ms-content-block__cta'>
                    {links.map((cta: ILinksData, index: number) => {
                        const btnClass = index === 0 ? 'msc-cta__primary' : 'msc-cta__secondary';
                        return (
                            <a
                                key={`link-${index}`}
                                className={btnClass}
                                role='button'
                                title={cta.linkText}
                                href={cta.linkUrl && cta.linkUrl.destinationUrl}
                                aria-label={cta.ariaLabel}
                                target={cta.openInNewTab ? '_blank' : undefined}
                                style={{
                                    backgroundColor: buttonBackgroundColor,
                                    borderColor: buttonBorderColor,
                                    color: buttonTextColor,
                                }} rel="noreferrer"
                            >
                                {cta.linkText}
                            </a>
                        );
                    })}
                </div>
            );
        }

        return null;
    }

    private _renderYouTubeLink(props: IContentBlockProps<{}>): JSX.Element | null {
        const modalId = `youtube-${props.config.youTubeLink}`;
        return (
            <div className='ms-content-block__cta ms-content-block__cta-youtube'>
                <div
                    className='msc-cta__primary'
                    role='button'
                    tabIndex={0}
                    onClick={this._toggleModal}
                    onKeyPress={this._toggleModal}
                >
                    {props.config.youTubeText || ''}
                </div>
                <Modal
                    isOpen={this.state.isModalOpen}
                    toggle={this._toggleModal}
                    modalClassName='youtube-modal'
                    aria-labelledby={modalId}
                >
                    <ModalHeader toggle={this._toggleModal} />
                    <ModalBody>
                        {this.state.isModalOpen &&
                            <YoutubeIFrame youtubeId={props.config.youTubeLink} />
                        }
                    </ModalBody>
                </Modal>
            </div>
        );
    }

    private _toggleModal(): void {
        this.setState(prevState => ({
            isModalOpen: !prevState.isModalOpen
        }));
    }
}

export default ContentBlockView;
